// src/components/RequireAuth.js
import React, { useEffect, useMemo, useState } from "react";
import { Navigate, useLocation } from "react-router-dom";
import { onAuthStateChanged } from "firebase/auth";
import { doc, getDoc } from "firebase/firestore";
import { auth, db } from "../firebase";

export default function RequireAuth({ children }) {
  const location = useLocation();

  const [checkingAuth, setCheckingAuth] = useState(true);
  const [checkingUserDoc, setCheckingUserDoc] = useState(false);
  const [user, setUser] = useState(null);
  const [mustChangePassword, setMustChangePassword] = useState(false);

  const isForcePasswordChangeRoute = location.pathname === "/force-password-change";

  useEffect(() => {
    const unsub = onAuthStateChanged(auth, (u) => {
      setUser(u || null);
      setCheckingAuth(false);
    });

    return () => unsub();
  }, []);

  useEffect(() => {
    let alive = true;

    const run = async () => {
      if (!user?.uid) {
        setMustChangePassword(false);
        setCheckingUserDoc(false);
        return;
      }

      setCheckingUserDoc(true);

      try {
        const userRef = doc(db, "users", user.uid);
        const userSnap = await getDoc(userRef);

        if (!alive) return;

        if (!userSnap.exists()) {
          setMustChangePassword(false);
        } else {
          const userData = userSnap.data() || {};
          setMustChangePassword(userData.mustChangePassword === true);
        }
      } catch (error) {
        console.error("RequireAuth user doc check failed:", error);
        if (alive) {
          setMustChangePassword(false);
        }
      }

      if (alive) {
        setCheckingUserDoc(false);
      }
    };

    run();

    return () => {
      alive = false;
    };
  }, [user, location.pathname, location.search]);

  const currentPathWithSearch = useMemo(() => {
    return `${location.pathname}${location.search || ""}`;
  }, [location.pathname, location.search]);

  if (checkingAuth || (user && checkingUserDoc)) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-zinc-950 text-zinc-300">
        Checking access...
      </div>
    );
  }

  if (!user) {
    const redirect = encodeURIComponent(currentPathWithSearch);
    return <Navigate to={`/login?redirect=${redirect}`} replace />;
  }

  if (mustChangePassword && !isForcePasswordChangeRoute) {
    const redirect = encodeURIComponent(currentPathWithSearch);
    return <Navigate to={`/force-password-change?redirect=${redirect}`} replace />;
  }

  if (!mustChangePassword && isForcePasswordChangeRoute) {
    const params = new URLSearchParams(location.search);
    const redirectParam = params.get("redirect");
    const redirect = redirectParam ? decodeURIComponent(redirectParam) : "/dashboard";
    return <Navigate to={redirect || "/dashboard"} replace />;
  }

  return children;
}